import {
  analysisMetricLabel,
  normalizeAnalysisJoinConfig,
  normalizeAnalysisLookupConfig,
  normalizeAnalysisRollupMetrics,
  type AnalysisRollupMetric,
} from '@/helpers/analysisMadeStep.helpers';
import type {
  AnalysisRecipeStep,
  AnalysisSheetRecipe,
} from '@/types/analysisRecipe.types';
import {
  formatSmallNumber,
  roundsToZero,
} from '@/helpers/smallNumberDisplay.helpers';
import { displayValueText } from '@/helpers/nestedValue.helpers';

export type AnalysisColumnKind =
  | 'lookup'
  | 'rollup'
  | 'join'
  | 'calc'
  | 'group'
  | 'aggregate'
  | 'window';

/**
 * How a computed value reads: a share of a total, a position, a measured number,
 * or as returned.
 *
 * `'number'` exists because a floating-point aggregate is not fit to print raw.
 * Summing 39 currency values accumulates binary error, and the grid was showing
 * `483261.16999999987` for a column the published card rendered as
 * `483,261.17` — the same value, formatted on one surface and not the other.
 */
export type AnalysisColumnFormat = 'percent' | 'rank' | 'number' | 'plain';

/**
 * The ceiling on author-chosen precision.
 *
 * `Intl.NumberFormat` throws a `RangeError` above 20 fraction digits, and a
 * double carries no more than about 17 significant digits anyway — past that
 * the extra places are the float's own noise, printed as if it were data.
 */
export const MAX_DISPLAY_DECIMALS = 10;

export interface AnalysisColumnDisplay {
  /** Short heading for the grid, the column picker, and CSV. */
  label: string;
  /** The recipe sentence behind the column, shown as the heading's tooltip. */
  description: string;
  kind: AnalysisColumnKind;
  format: AnalysisColumnFormat;
  /**
   * Author-chosen decimal places, from the sheet's `columnPrecision`. Absent
   * means the format's own default applies — this is display precision, so
   * absent is "unset", never zero.
   */
  decimals?: number;
}

export type AnalysisColumnDisplayMap = Record<string, AnalysisColumnDisplay>;

const stringConfig = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null;

/**
 * How a calculated column reads.
 *
 * Everything that was not `pctOf` used to be `plain`, so an arithmetic result —
 * a difference, a sum, a product, a division — carried no format at all and
 * therefore got none of the numeric affordances: no grouping separators, no
 * float-noise cap, and no decimal-places control, which is the visible symptom.
 *
 * The split mirrors `analysisColumnIsNumeric`'s calc branch deliberately: a
 * column numeric enough to summarise is numeric enough to format, and keeping
 * one taxonomy means the two cannot drift apart. `concat` joins text, a flag
 * emits the literal 'Yes'/'No', and `coalesce` is whatever its operands were —
 * none of those are numbers.
 */
const calcColumnFormat = (step: AnalysisRecipeStep): AnalysisColumnFormat => {
  const operator = step.config.operator;
  if (operator === 'pctOf') return 'percent';
  if (step.config.flagCondition !== undefined || operator === undefined) {
    return 'plain';
  }
  if (operator === 'concat' || operator === 'coalesce') return 'plain';
  return 'number';
};

const stepDisplays = (sheet: AnalysisSheetRecipe): AnalysisColumnDisplayMap => {
  const displays: AnalysisColumnDisplayMap = {};
  sheet.steps.forEach((step) => {
    const targetLabel = stringConfig(step.config.targetLabel);
    if (step.kind === 'lookup') {
      const config = normalizeAnalysisLookupConfig(step);
      const configuredOutputs = (config.configuredOutputs ?? []).flatMap(
        (output) =>
          output.outputColumn
            ? [
                {
                  column: output.outputColumn,
                  field: output.field,
                  owner:
                    output.hopIndex !== null && output.hopIndex >= 0
                      ? (config.path.hops[output.hopIndex]?.targetLabel ??
                        targetLabel)
                      : targetLabel,
                },
              ]
            : [],
      );
      const legacyOutputs = [
        ...config.intermediateOutputs.map((entry, index) => {
          const ownerHop =
            entry.hopIndex !== null && entry.hopIndex >= 0
              ? config.path.hops[entry.hopIndex]
              : null;
          return {
            column: entry.outputColumn ?? step.outputColumns[index],
            field: entry.field,
            owner: ownerHop?.targetLabel ?? null,
          };
        }),
        ...config.targetFields.map((field, index) => ({
          column: step.outputColumns[config.intermediateOutputs.length + index],
          field,
          owner: targetLabel,
        })),
      ];
      const lookupOutputs =
        configuredOutputs.length > 0 ? configuredOutputs : legacyOutputs;
      lookupOutputs.forEach(({ column, field, owner }) => {
        if (!column) return;
        // A looked-up `name` sitting beside the source's own `name` has to say
        // which record it came from, or the two headings are indistinguishable.
        displays[column] = {
          label: owner ? `${field} (${owner})` : field,
          description: step.sentence,
          kind: 'lookup',
          format: 'plain',
        };
      });
      return;
    }
    if (step.kind === 'rollup') {
      normalizeAnalysisRollupMetrics(step).forEach((value) => {
        const metric = value.metric;
        const field = value.field;
        const metricLabel =
          value.outputName ?? analysisMetricLabel(metric, field);
        const column = value.outputColumn;
        if (!column) return;
        displays[column] = {
          label: targetLabel ? `${metricLabel} (${targetLabel})` : metricLabel,
          description: step.sentence,
          kind: 'rollup',
          format: 'plain',
        };
      });
      return;
    }
    if (step.kind === 'join') {
      const config = normalizeAnalysisJoinConfig(step);
      config?.outputs.forEach(({ field, outputColumn }) => {
        if (!outputColumn) return;
        // Same reasoning as a lookup's heading: a joined `pump_name` sitting
        // beside the source's own has to say which record type it came from.
        displays[outputColumn] = {
          label: `${field} (${config.targetLabel})`,
          description: step.sentence,
          kind: 'join',
          format: 'plain',
        };
      });
      return;
    }
    // Only a calc reaches here. Kept explicit rather than as a fallthrough:
    // a join step has no `outputColumn`, so when it DID fall through it was
    // labelled with `column` — the encoded `analysis__…` key, in the grid
    // heading. A new step kind must fail to be displayed rather than inherit
    // a heading built for a different shape.
    if (step.kind !== 'calc') return;
    const column = step.outputColumns[0];
    if (!column) return;
    displays[column] = {
      label: stringConfig(step.config.outputColumn) ?? column,
      description: step.sentence,
      kind: 'calc',
      format: calcColumnFormat(step),
    };
  });
  return displays;
};

/**
 * Display labels for a sheet's computed columns, keyed by the internal column
 * key the pipeline and rows use (`analysis__<encoded-step>__<encoded-key>`).
 *
 * Nothing derives a heading from the key itself: it exists to be unique across
 * steps, so showing it puts an encoded implementation key in front of the user.
 * A breakdown's group column is named after a parent column, so the parent's
 * own labels are resolved first and reused here.
 */
/**
 * Stamps the sheet's chosen precision onto every display it names.
 *
 * Applied once, at the end, rather than at each of the six places a display is
 * built: a column the author set a precision for must carry it whatever kind of
 * column it is, and a rule spread across the builders is a rule one of them will
 * eventually be written without.
 */
const withColumnPrecision = (
  displays: AnalysisColumnDisplayMap,
  columnPrecision: Record<string, number> | undefined,
): AnalysisColumnDisplayMap => {
  if (!columnPrecision) return displays;
  Object.entries(columnPrecision).forEach(([column, decimals]) => {
    const display = displays[column];
    if (!display || !Number.isFinite(decimals)) return;
    displays[column] = { ...display, decimals };
  });
  return displays;
};

export function buildAnalysisColumnDisplay(
  sheet: AnalysisSheetRecipe,
  parentSheet?: AnalysisSheetRecipe | null,
): AnalysisColumnDisplayMap {
  const displays = stepDisplays(sheet);
  const breakdown = sheet.breakdown;
  if (!breakdown) return withColumnPrecision(displays, sheet.columnPrecision);

  const parentDisplays = parentSheet ? stepDisplays(parentSheet) : {};
  // A related-record grouping's key is a freshly minted encoded id with no
  // parent column behind it — the parent lookup never resolves — so its own
  // `targetLabel` is the readable name instead of the raw key falling through.
  // A total sheet has no group column. Writing the block anyway would key it
  // under the literal string "undefined" and describe "One row per undefined".
  const groupByColumn = breakdown.groupByColumn;
  const groupLabel = breakdown.groupByRelated
    ? breakdown.groupByRelated.targetLabel
    : groupByColumn
      ? (parentDisplays[groupByColumn]?.label ?? groupByColumn)
      : null;
  if (groupByColumn && groupLabel) {
    displays[groupByColumn] = {
      label: groupLabel,
      description: breakdown.groupByRelated
        ? `One row per ${groupLabel}, plus a "No ${groupLabel}" group for rows with no match`
        : `One row per ${groupLabel}`,
      kind: 'group',
      format: 'plain',
    };
  }
  (breakdown.aggregations ?? []).forEach((aggregation) => {
    const sourceLabel = aggregation.field
      ? (parentDisplays[aggregation.field]?.label ?? aggregation.field)
      : undefined;
    const label = sourceLabel
      ? `${sourceLabel} · ${aggregation.op}`
      : `rows · ${aggregation.op}`;
    // `count_distinct` counts distinct source rows, not source-row-to-group
    // links — a source reaching the same group through more than one edge
    // must not inflate it. On a related grouping that also means one row can
    // legitimately count in more than one group, so the description says so
    // rather than reading like an ordinary row count.
    const countDescription =
      aggregation.op === 'count_distinct'
        ? `Distinct source rows connected to each ${groupLabel} — a row connected to more than one group is counted in each, so totals can exceed the row count`
        : `Count of source rows per ${groupLabel}`;
    displays[aggregation.outputColumn] = {
      label,
      description: sourceLabel
        ? `${analysisMetricLabel(
            aggregation.op as AnalysisRollupMetric,
            sourceLabel,
          )} per ${groupLabel}`
        : countDescription,
      kind: 'aggregate',
      // `count` is an exact integer and needs no help; `sum`/`avg`/`min`/`max`
      // over floats do. `'number'` is harmless on an integer either way — it only
      // adds grouping separators — so this does not have to guess which
      // aggregate produced a whole number.
      format: 'number',
    };
  });
  (breakdown.windows ?? []).forEach((window) => {
    const sourceLabel =
      displays[window.sourceColumn]?.label ?? window.sourceColumn;
    displays[window.outputColumn] =
      window.op === 'percent_of_total'
        ? {
            label: `% of total ${sourceLabel}`,
            description: `Each row's ${sourceLabel} as a share of the sheet total`,
            kind: 'window',
            format: 'percent',
          }
        : {
            label: `Rank by ${sourceLabel}`,
            description: `Position by ${sourceLabel}, highest first`,
            kind: 'window',
            format: 'rank',
          };
  });
  return withColumnPrecision(displays, sheet.columnPrecision);
}

/** Each display's heading, keyed by its column. */
export const analysisColumnLabels = (
  displays: AnalysisColumnDisplayMap,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(displays).map(([column, display]) => [
      column,
      display.label,
    ]),
  );

/**
 * A computed cell as text, or `null` when the column has no format of its own
 * and the table's normal rendering should apply.
 *
 * Percent-of-total arrives as 0–100 from `window_rows`, so it is shown with a
 * sign and one decimal instead of a bare float that could be read as a ratio.
 */
export function formatAnalysisCellValue(
  value: unknown,
  format: AnalysisColumnFormat | undefined,
  /**
   * Author-chosen decimal places. Absent keeps each format's own default —
   * one decimal for a percentage, `toLocaleString` for a measured number.
   */
  decimals?: number,
): string | null {
  if (!format || format === 'plain') return null;
  if (value === null || value === undefined || value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const chosen =
    typeof decimals === 'number' && Number.isFinite(decimals)
      ? Math.min(Math.max(Math.trunc(decimals), 0), MAX_DISPLAY_DECIMALS)
      : null;
  if (format === 'percent') {
    const places = chosen ?? 1;
    // A share that is real but smaller than the chosen precision must not be
    // shown as `0.0%`: that reads as "none of them", which is a different
    // answer, and it is exactly the case this control exists for. Significant
    // digits keep the magnitude visible however small it is, and the author can
    // still raise the precision to see it in place.
    if (roundsToZero(numeric, places)) {
      return `${formatSmallNumber(numeric)}%`;
    }
    return `${numeric.toFixed(places)}%`;
  }
  // A rank is a position. Fractional places would be meaningless, so this format
  // deliberately ignores the setting rather than pretending to honour it.
  if (format === 'rank') return String(Math.round(numeric));
  if (chosen !== null) {
    if (roundsToZero(numeric, chosen)) {
      return formatSmallNumber(numeric);
    }
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: chosen,
      maximumFractionDigits: chosen,
    });
  }
  // The same call the KPI tile makes (`KpiTileRenderer`'s `formatKpiValue`), so a
  // sum reads identically on the sheet and on the card published from it. Its
  // defaults are the point: grouping separators, and at most three fraction
  // digits with trailing zeros dropped. That caps float noise without inventing
  // precision — an integer sum stays `39`, not `39.000`.
  const grouped = numeric.toLocaleString();
  // Three fraction digits are too few for a genuinely small value: `0.0004`
  // formats to `0`, which is not noise-free — it is wrong, and reads as real
  // data rather than as a rounding artifact. Anything that rounds to nothing
  // while not being nothing falls back to significant digits, which keeps the
  // magnitude visible however small it is. (`formatKpiValue` shares this gap;
  // matching it here would mean knowingly printing a false zero.)
  //
  // Tested against the number, not against the formatted string: a locale that
  // groups with `.` and decimalises with `,` ("483.261,17") cannot be parsed back
  // by stripping separators, so a string round-trip would misjudge the check
  // outside en-US. `0.0005` is the threshold three fraction digits round at.
  if (numeric !== 0 && Math.abs(numeric) < 0.0005) {
    return numeric.toLocaleString(undefined, { maximumSignificantDigits: 3 });
  }
  return grouped;
}

/**
 * A cell as it should appear in an exported file.
 *
 * The same three-step chain the grid cell uses — the column's own Analysis
 * format, then Neo4j's own types, then the compact form for anything nested —
 * so a value cannot read one way on screen and another in the file. The export
 * had only the first step and a bare `String`, which wrote the literal
 * `[object Object]` for any map or list, and a Neo4j Date as its internals.
 */
export function formatAnalysisExportValue(
  value: unknown,
  format: AnalysisColumnFormat | undefined,
  decimals?: number,
): string {
  const analysisFormatted = formatAnalysisCellValue(value, format, decimals);
  if (analysisFormatted !== null) return analysisFormatted;
  return displayValueText(value);
}
